import React, { useCallback, useMemo, useRef, useState } from 'react';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { Phone, CheckCircle2 } from 'lucide-react';
import { AxiosError } from 'axios';
import { Button } from '../UI/Button';
import { otpApi } from '../../api/otp';
import styles from './PhoneVerificationFlow.module.css';

type Step = 'phone' | 'code' | 'verified';

interface Props {
  onVerified: (phoneE164: string) => void;
  defaultPhone?: string;
}

const OTP_LENGTH = 6;

function normalize(raw: string): string | null {
  const parsed = parsePhoneNumberFromString(raw, 'IN');
  return parsed?.isValid() ? parsed.number : null;
}

function extractError(err: unknown, fallback: string): string {
  if (err instanceof AxiosError) {
    const data = err.response?.data as { error?: string } | undefined;
    if (data?.error) return data.error;
    if (err.response?.status === 429) return 'Too many attempts, please try again in a minute.';
  }
  return fallback;
}

export const PhoneVerificationFlow: React.FC<Props> = ({ onVerified, defaultPhone = '' }) => {
  const [step, setStep] = useState<Step>('phone');
  const [phoneInput, setPhoneInput] = useState(defaultPhone);
  const [normalizedPhone, setNormalizedPhone] = useState<string | null>(null);
  const [digits, setDigits] = useState<string[]>(() => Array(OTP_LENGTH).fill(''));
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const digitRefs = useRef<Array<HTMLInputElement | null>>([]);

  const phoneValid = useMemo(() => normalize(phoneInput) !== null, [phoneInput]);

  const handleSend = useCallback(async () => {
    if (sending) return;
    const normalized = normalize(phoneInput);
    if (!normalized) {
      setError('Please enter a valid phone number with country code.');
      return;
    }
    setError(null);
    setSending(true);
    try {
      await otpApi.request(normalized);
      setNormalizedPhone(normalized);
      setDigits(Array(OTP_LENGTH).fill(''));
      setStep('code');
      setTimeout(() => digitRefs.current[0]?.focus(), 50);
    } catch (err) {
      setError(extractError(err, 'Unable to send code. Please try again.'));
    } finally {
      setSending(false);
    }
  }, [phoneInput, sending]);

  const handleVerify = useCallback(
    async (code: string) => {
      if (!normalizedPhone || verifying) return;
      if (code.length !== OTP_LENGTH) return;
      setError(null);
      setVerifying(true);
      try {
        const result = await otpApi.verify(normalizedPhone, code);
        if (result.status === 'approved') {
          setStep('verified');
          onVerified(normalizedPhone);
        } else {
          setError('Invalid code. Please try again.');
        }
      } catch (err) {
        setError(extractError(err, 'Invalid code. Please try again.'));
      } finally {
        setVerifying(false);
      }
    },
    [normalizedPhone, onVerified, verifying],
  );

  const handleDigitChange = (index: number, value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (!cleaned) {
      setDigits((prev) => {
        const next = [...prev];
        next[index] = '';
        return next;
      });
      return;
    }
    setDigits((prev) => {
      const next = [...prev];
      // Handle paste — distribute across remaining inputs
      for (let i = 0; i < cleaned.length && index + i < OTP_LENGTH; i++) {
        next[index + i] = cleaned[i];
      }
      return next;
    });
    const nextIndex = Math.min(index + cleaned.length, OTP_LENGTH - 1);
    digitRefs.current[nextIndex]?.focus();
    const combined = [...digits];
    for (let i = 0; i < cleaned.length && index + i < OTP_LENGTH; i++) {
      combined[index + i] = cleaned[i];
    }
    if (combined.every((d) => d !== '') && combined.join('').length === OTP_LENGTH) {
      void handleVerify(combined.join(''));
    }
  };

  const handleDigitKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      digitRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) digitRefs.current[index - 1]?.focus();
    if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) digitRefs.current[index + 1]?.focus();
  };

  if (step === 'verified') {
    return (
      <div className={styles.wrapper}>
        <div className={styles.verified}>
          <CheckCircle2 className={styles['verified-icon']} />
          <span>Phone verified: {normalizedPhone}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <p className={styles.header}>
        What's your phone number?
        <span className={styles['required-badge']}>Required</span>
      </p>

      <div className={styles['phone-row']}>
        <div className={styles['phone-input-wrap']}>
          <Phone className={styles['phone-icon']} />
          <input
            type="tel"
            inputMode="tel"
            value={phoneInput}
            onChange={(e) => setPhoneInput(e.target.value)}
            placeholder="+91 98765 43210"
            className={styles['phone-input']}
            disabled={sending || step === 'code'}
          />
        </div>
        {step === 'phone' && (
          <div className={styles['send-button']}>
            <Button
              variant="primary"
              size="md"
              onClick={handleSend}
              loading={sending}
              disabled={!phoneValid || sending}
            >
              Send code
            </Button>
          </div>
        )}
      </div>

      <p className={styles.hint}>
        We'll text you a 6-digit verification code. Standard messaging rates apply.
      </p>

      {step === 'code' && (
        <>
          <p className={styles['otp-label']}>Enter the 6-digit code</p>
          <div className={styles['otp-row']}>
            {digits.map((digit, i) => (
              <input
                key={i}
                ref={(el) => {
                  digitRefs.current[i] = el;
                }}
                type="text"
                inputMode="numeric"
                maxLength={i === 0 ? OTP_LENGTH : 1}
                value={digit}
                onChange={(e) => handleDigitChange(i, e.target.value)}
                onKeyDown={(e) => handleDigitKeyDown(i, e)}
                className={styles['otp-digit']}
                disabled={verifying}
                aria-label={`Digit ${i + 1}`}
              />
            ))}
          </div>
          <div className={styles['actions-row']}>
            <button
              type="button"
              className={styles['resend-link']}
              onClick={() => {
                setStep('phone');
                setError(null);
              }}
              disabled={sending || verifying}
            >
              ← Change number
            </button>
            <button
              type="button"
              className={styles['resend-link']}
              onClick={handleSend}
              disabled={sending || verifying}
            >
              Resend code
            </button>
          </div>
        </>
      )}

      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
};
